import React from 'react'

import { routes } from '@sketch/modules-common'

import { useGetPublications } from '../../hooks/useGetPublications'

import {
  Skeleton,
  ErrorMessage,
  Grid,
  GridColumnProps,
} from '@sketch/components'

import { GridPublicationItem } from '../../components/GridPublicationItem'

import {
  EmptyState,
  StyledGrid,
  StyledSkeleton,
} from './WorkspaceProfilePublications.styles'

import { getGridColumnSize } from '../../utils'
import { WorkspaceProfileFragment } from '@sketch/gql-types'

interface WorkspaceProfileLayoutProps {
  workspaceId: string
  profile: WorkspaceProfileFragment
}

const GUTTER = { xl: 40, lg: 40, md: 40, sm: 40, xs: 24 }
const GRID_ROW_GAP = { xl: 64, lg: 64, md: 64, sm: 32, xs: 32 }
const COLUMN_BREAKPOINTS = {
  lg: 4,
  md: 6,
  sm: 12,
  xs: 12,
  xxs: 12,
} as GridColumnProps

const createArray = (numberOfItems: number) =>
  Array.from(Array(numberOfItems).keys())

const DocumentGridSkeleton = () => (
  <Grid gutter={GUTTER} gridRowGap={GRID_ROW_GAP}>
    {createArray(6).map(placeholder => {
      return (
        <Grid.Column
          key={`placeholder-skeleton-${placeholder}`}
          {...COLUMN_BREAKPOINTS}
        >
          <Skeleton width="100%" height="225px" />
          <StyledSkeleton width="40%" height="14px" />
        </Grid.Column>
      )
    })}
  </Grid>
)

export const WorkspaceProfilePublications = (
  props: WorkspaceProfileLayoutProps
) => {
  const { workspaceId, profile } = props

  const { publications, placeholders, isEmpty, error, loading } =
    useGetPublications(workspaceId)

  if (loading) {
    return <DocumentGridSkeleton />
  }

  if (isEmpty) {
    return <EmptyState workspaceName={profile?.workspace.name!} />
  }

  if (error) {
    return <ErrorMessage.Generic />
  }

  const isSingleDocument = publications.length === 1
  const isDoubleDocument = publications.length === 2

  if (isDoubleDocument) {
    return (
      <Grid asList gutter={GUTTER} gridRowGap={GRID_ROW_GAP}>
        <Grid.Column asListItem key="spacer-l" xs={2} xxs={12} />
        <Grid.Column
          asListItem
          key={`profile-document-${publications[0].share.identifier}`}
          xs={4}
          xxs={12}
        >
          <GridPublicationItem
            publication={publications[0]}
            url={routes.WORKSPACE_PROFILE_DOCUMENT.create({
              shortUrlName: profile!.shortUrlName,
              publicationId: publications[0].identifier,
            })}
          />
        </Grid.Column>
        <Grid.Column
          asListItem
          key={`profile-document-${publications[1].share.identifier}`}
          xs={4}
          xxs={12}
        >
          <GridPublicationItem
            publication={publications[1]}
            url={routes.WORKSPACE_PROFILE_DOCUMENT.create({
              shortUrlName: profile!.shortUrlName,
              publicationId: publications[0].identifier,
            })}
          />
        </Grid.Column>
        <Grid.Column asListItem key="spacer-r" xs={2} xxs={12} />
      </Grid>
    )
  }

  return (
    <StyledGrid asList gutter={GUTTER} gridRowGap={GRID_ROW_GAP}>
      {publications.map(publication => {
        const { identifier: publicationId, share } = publication

        /*
         * For single documents, we want to avoid HUGE thumbnails and keep the item centered.
         * We are setting column breakpoints specifically for this case.
         */
        if (isSingleDocument) {
          return (
            <React.Fragment key="spacer-l">
              <Grid.Column sm={4} xs={3} xxs={12} />
              <Grid.Column
                key={`profile-document-${share.identifier}`}
                sm={4}
                xs={6}
                xxs={12}
              >
                <GridPublicationItem
                  publication={publication}
                  url={routes.WORKSPACE_PROFILE_DOCUMENT.create({
                    shortUrlName: profile!.shortUrlName,
                    publicationId,
                  })}
                />
              </Grid.Column>
              <Grid.Column sm={4} xs={3} xxs={12} />
            </React.Fragment>
          )
        }

        return (
          <Grid.Column
            asListItem
            key={`profile-document-${share.identifier}`}
            {...COLUMN_BREAKPOINTS}
          >
            <GridPublicationItem
              publication={publication}
              url={routes.WORKSPACE_PROFILE_DOCUMENT.create({
                shortUrlName: profile!.shortUrlName,
                publicationId,
              })}
            />
          </Grid.Column>
        )
      })}

      {placeholders.map((placeholder, placeholderIndex) => {
        const index = publications.length + placeholderIndex

        const columnSize = getGridColumnSize(index)

        return (
          <Grid.Column
            asListItem
            key={`placeholder-${index}-${columnSize}`}
            {...COLUMN_BREAKPOINTS}
          >
            {placeholder}
          </Grid.Column>
        )
      })}
    </StyledGrid>
  )
}
